import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Box,
  Chip
} from '@mui/material';
import {
  Public as PublicIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  ManageAccounts as ManageIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as RoleIcon
} from '@mui/icons-material';
import apiService from '../services/apiService';

const PermissionTest = () => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});

  const testEndpoint = async (key, apiCall, description) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    setResults(prev => ({ ...prev, [key]: null }));

    try {
      const data = await apiCall();
      setResults(prev => ({
        ...prev,
        [key]: {
          success: true,
          data: data,
          status: 200,
          message: 'Success'
        }
      }));
    } catch (error) {
      console.error(`Error testing ${key}:`, error);
      setResults(prev => ({
        ...prev,
        [key]: {
          success: false,
          status: error.response?.status || 500,
          message: error.response?.status === 403 
            ? 'Access Denied - Insufficient Permissions'
            : error.response?.status === 401
            ? 'Unauthorized - Authentication Required'
            : error.message || 'Unknown Error'
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const tests = [
    {
      key: 'public',
      title: 'Public Endpoint',
      description: 'No authentication required',
      icon: <PublicIcon />,
      color: 'success',
      apiCall: () => apiService.getPublicData()
    },
    {
      key: 'authenticated',
      title: 'Authenticated Endpoint',
      description: 'Requires valid login only',
      icon: <LockIcon />,
      color: 'info',
      apiCall: () => apiService.getAuthenticatedData()
    },
    {
      key: 'viewEmployees',
      title: 'View Employees',
      description: 'Requires "ViewEmployees" permission',
      icon: <PeopleIcon />,
      color: 'primary',
      apiCall: () => apiService.getEmployeesData()
    },
    {
      key: 'manageEmployees',
      title: 'Manage Employees',
      description: 'Requires "ManageEmployees" permission',
      icon: <ManageIcon />,
      color: 'warning',
      apiCall: () => apiService.getManagerData()
    },
    {
      key: 'adminOnly',
      title: 'Admin Only',
      description: 'Requires "SystemAdmin" permission',
      icon: <AdminIcon />,
      color: 'error',
      apiCall: () => apiService.getAdminData()
    },
    {
      key: 'roles',
      title: 'View Roles',
      description: 'Role management access',
      icon: <RoleIcon />,
      color: 'secondary',
      apiCall: () => apiService.getRoles()
    },
    {
      key: 'debugToken',
      title: 'Debug Token',
      description: 'Show token payload for debugging',
      icon: <AdminIcon />,
      color: 'info',
      apiCall: () => apiService.getDebugToken()
    }
  ];

  const getStatusChip = (result) => {
    if (!result) return null;
    
    if (result.success) {
      return <Chip label={`${result.status} Success`} color="success" size="small" />;
    } else {
      const color = result.status === 403 ? 'warning' : result.status === 401 ? 'error' : 'default';
      return <Chip label={`${result.status} ${result.status === 403 ? 'Forbidden' : result.status === 401 ? 'Unauthorized' : 'Error'}`} color={color} size="small" />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Permission System Test
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Test different API endpoints to verify permission-based access control.
      </Typography>

      <Grid container spacing={3}>
        {tests.map((test) => (
          <Grid item xs={12} md={6} key={test.key}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: `${test.color}.main`, mr: 1 }}>
                    {test.icon}
                  </Box>
                  <Typography variant="h6" component="h2">
                    {test.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {test.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    color={test.color}
                    onClick={() => testEndpoint(test.key, test.apiCall, test.description)}
                    disabled={loading[test.key]}
                    size="small"
                  >
                    {loading[test.key] ? 'Testing...' : 'Test'}
                  </Button>
                  {getStatusChip(results[test.key])}
                </Box>

                {results[test.key] && (
                  <Alert 
                    severity={results[test.key].success ? 'success' : 'error'}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="body2">
                      <strong>{results[test.key].message}</strong>
                    </Typography>
                    {results[test.key].success && results[test.key].data && (
                      <Typography variant="caption" component="div" sx={{ mt: 1, fontFamily: 'monospace' }}>
                        {typeof results[test.key].data === 'string' 
                          ? results[test.key].data 
                          : JSON.stringify(results[test.key].data, null, 2)}
                      </Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          How to interpret results:
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>200 Success</strong> - You have the required permission<br/>
          • <strong>403 Forbidden</strong> - You don't have the required permission<br/>
          • <strong>401 Unauthorized</strong> - Authentication issue<br/>
          • <strong>500 Error</strong> - Server error
        </Typography>
      </Box>
    </Container>
  );
};

export default PermissionTest;
